import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { RefreshCcw, Repeat, Search } from 'lucide-react'
import { apiClient } from '../services/api'
import { useTranslation } from '../contexts/TranslationContext'
import Button from '../components/ui/Button'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { ui } from '../styles/ui'
import { formatDateTime } from '../utils/date'

export default function ProductAnalysisQueue() {
  const { t } = useTranslation()
  const [receipts, setReceipts] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('pending')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const loadReceipts = async (pageNumber = 1) => {
    try {
      setLoading(true)
      const params = { page: pageNumber }
      if (search) params.search = search
      if (status) params.status = status

      const response = await apiClient.get('/queue/product-analysis', { params })
      setReceipts(response.data.data || [])
      setPage(response.data.current_page || 1)
      setTotalPages(response.data.last_page || 1)
    } catch (err) {
      console.error('Failed to load product analysis queue', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReceipts(1)
  }, [])

  useEffect(() => {
    loadReceipts(1)
  }, [status])

  useEffect(() => {
    const interval = setInterval(() => {
      loadReceipts(page)
    }, 30000)

    return () => clearInterval(interval)
  }, [page, status, search])

  const retryReceipt = async (receiptId) => {
    try {
      await apiClient.post(`/queue/product-analysis/${receiptId}/retry`)
      loadReceipts(page)
    } catch (err) {
      console.error('Failed to retry product analysis', err)
    }
  }

  return (
    <PageShell>
      <PageHeader
        title={t('Product Analysis Queue', 'Product Analysis Queue')}
        subtitle={t('Monitor receipts waiting for product categorization and insertion.', 'Monitor receipts waiting for product categorization and insertion.')}
        actions={
          <Button variant="ghost" onClick={() => loadReceipts(1)}>
            <RefreshCcw size={16} className="mr-1" /> {t('Refresh', 'Refresh')}
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <div className={`${ui.card} ${ui.cardBody}`}>
          <label className={`${ui.label} mb-2`}>{t('Search', 'Search')}</label>
          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadReceipts(1)}
              className={ui.input}
              placeholder={t('Receipt ID or file name', 'Receipt ID or file name')}
            />
            <Button type="submit" onClick={() => loadReceipts(1)}>
              <Search size={16} />
            </Button>
          </div>
        </div>

        <div className={`${ui.card} ${ui.cardBody}`}>
          <label className={`${ui.label} mb-2`}>{t('Status', 'Status')}</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={ui.select}
          >
            <option value="">{t('All', 'All')}</option>
            <option value="pending">{t('Pending', 'Pending')}</option>
            <option value="processing">{t('Processing', 'Processing')}</option>
            <option value="completed">{t('Completed', 'Completed')}</option>
            <option value="failed">{t('Failed', 'Failed')}</option>
          </select>
        </div>
      </div>

      <div className={ui.card}>
        <table className={ui.table}>
          <thead className={ui.tableHead}>
            <tr>
              <th className={ui.tableHeadCell}>{t('Receipt ID', 'Receipt ID')}</th>
              <th className={ui.tableHeadCell}>{t('User', 'User')}</th>
              <th className={ui.tableHeadCell}>{t('File', 'File')}</th>
              <th className={ui.tableHeadCell}>{t('Product Analysis', 'Product Analysis')}</th>
              <th className={ui.tableHeadCell}>{t('OCR Status', 'OCR Status')}</th>
              <th className={ui.tableHeadCell}>{t('Created', 'Created')}</th>
              <th className={ui.tableHeadCell}>{t('Actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-10">
                  <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
                    <div className={ui.spinner} />
                    <div className="text-sm">{t('Loading queue...', 'Loading queue...')}</div>
                  </div>
                </td>
              </tr>
            ) : receipts.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">{t('No receipts found.', 'No receipts found.')}</td>
              </tr>
            ) : (
              receipts.map((receipt) => (
                <tr key={receipt.id} className="border-t border-divider hover:bg-surface-elevated">
                  <td className={ui.tableCell}><span className="font-medium text-foreground">{receipt.id}</span></td>
                  <td className={ui.tableCellText}>{receipt.user?.email || receipt.user?.name || t('N/A', 'N/A')}</td>
                  <td className={ui.tableCellText}>{receipt.file_name}</td>
                  <td className={ui.tableCellText}>{receipt.product_analysis_status}</td>
                  <td className={ui.tableCellText}>{receipt.ocr_status}</td>
                  <td className={ui.tableCellText}>{formatDateTime(receipt.created_at)}</td>
                  <td className={ui.tableCell}>
                    <div className="flex gap-1">
                      <Link to={`/admin/jobs/${receipt.id}`} className={ui.buttonSmGhost}>
                        {t('View', 'View')}
                      </Link>
                      <Button size="sm" onClick={() => retryReceipt(receipt.id)}>
                        <Repeat size={14} /> {t('Retry', 'Retry')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-2 py-3 text-sm text-muted-foreground">
        <Button variant="ghost" onClick={() => loadReceipts(Math.max(1, page - 1))} disabled={page <= 1}>
          {t('Previous', 'Previous')}
        </Button>
        <span>{t('Page', 'Page')} {page} / {totalPages}</span>
        <Button variant="ghost" onClick={() => loadReceipts(Math.min(totalPages, page + 1))} disabled={page >= totalPages}>
          {t('Next', 'Next')}
        </Button>
      </div>
    </PageShell>
  )
}

